import { Injectable }             from '@angular/core';
import { Response }               from '@angular/http';
import { FormGroup, FormBuilder,
        Validators, FormControl } from '@angular/forms';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Observable }             from 'rxjs/Observable';

import { Wave }                   from './../../models/program/wave';
import { Group }                  from './../../models/program/group';
import { GroupUsers }             from './../../models/program/group-users';
import { GroupSingle }            from './../../models/program/group-single';
import { WaveSingle }             from './../../models/program/wave-single';
import { User }                   from './../../models/program/user';
import { UserWelcomeMessage }     from './../../models/program/user-welcome-message';
import { BatchCommitInfo }             from '../../models/program/batch-commit-info';

import { ApiService }             from '../../root-services/api.service';

@Injectable()

export class GroupService {
  private programsUrl = '/programs';

  public user: User;
  public _groups: Observable<Group[]> = null;
  public _waves: Observable<Wave[]> = null;
  public _groupSingle: Observable<GroupSingle> = null;
  public _groupUsers: Observable<GroupUsers[]> = null;
  public _programId: number = null;
  public _groupId: number = null;

  constructor(private api: ApiService, private _fb: FormBuilder) { }

  /**
   * Group Methods
   */

  // Get all groups in a program
  public getAllGroups(id: number): Observable<Group[]> {
    return this.api.get(`${this.programsUrl}/${id}/groups`)
                   .map((response: Response) => response.json() as Group[])
                   .publishReplay(1).refCount()
                   .catch(this.handleError);
  }

  // Get all group in a program that are not in a wave
  public getLoneGroups(id: number, state?: string): Observable<Group[]> {
    // if (id !== this._programId) {
      // this._programId = id;
    let url = `${this.programsUrl}/${id}/lone_groups`;
    if(state){
      url += '?state=' + state;
    }
    this._groups = this.api.get(url)
                            .map((response: Response) => response.json() as Group[])
                            .publishReplay(1).refCount()
                            .catch(this.handleError);
    // }

    return this._groups;
  }

  // Get a single group in a program by its ID
  public getGroup(programId: number, groupId: number): Observable<GroupSingle> {
    this._groupSingle = this.api.get(`${this.programsUrl}/${programId}/groups/${groupId}`)
                                .map((response: Response) => response.json() as GroupSingle)
                                .publishReplay(1).refCount()
                                .catch(this.handleError);

    return this._groupSingle;
  }

  public createFromExisting(programId: number, body: any): Observable<any> {
    return this.api.post(`${this.programsUrl}/${programId}/actions/create_group_with_users`, body)
                   .map((response: Response) => response.json())
                   .publishReplay(1).refCount()
                   .catch(this.handleError);
  }

  // Get a single group's settings (used for settings modal)
  public getSettings(programId: number, groupId: number): Observable<GroupSingle> {
    return this.api.get(`${this.programsUrl}/${programId}/groups/${groupId}`)
                   .map((response: Response) => response.json() as GroupSingle)
                   .publishReplay(1).refCount()
                   .catch(this.handleError);
  }

  // Edit a group's settings
  public editSettings(programId: number, groupId: number, body: any): Observable<GroupSingle> {
    return this.api.patch(`${this.programsUrl}/${programId}/groups/${groupId}`, body)
                   .publishReplay(1).refCount()
                   .catch(this.handleError);
  }

  // Get all users in a group
  public getGroupUsers(programId: number, groupId: number, reset?: boolean): Observable<GroupUsers[]> {
    if (reset) { this._groupId = null; }
    if (groupId !== this._groupId) {
      this._groupId = groupId;
      this._groupUsers = this.api.get(`${this.programsUrl}/${programId}/groups/${groupId}/users`)
                                 .map((response: Response) => response.json() as GroupUsers[])
                                 .publishReplay(1).refCount()
                                 .catch(this.handleError);
    }

    return this._groupUsers;
  }

  // Add a user to a group.
  public addUser(programId: number, groupId: number, user: User): Promise<User> {
    return this.api.post(`${this.programsUrl}/${programId}/groups/${groupId}/users`, user)
                   .toPromise()
                   .then(response => response.json())
                   .catch(this.handleError);
  }

  // Delete group/groups. Body sent is an array of id's.
  public deleteGroup(programId: number, groupIds: number[]): Observable<Response> {
    let body = { 'group_ids': groupIds };
    return this.api.post(`${this.programsUrl}/${programId}/actions/bulk_delete_groups`, body)
               .map(() => {})
               .catch(this.handleError);
  }

  // Move a group/groups from one wave to another or to lone. Body is an array of id's.
  public moveGroups(programId: number, body: { group_ids: number[]; wave_id: number | null}): Observable<Response> {
    return this.api.post(`${this.programsUrl}/${programId}/actions/bulk_move_groups`, body)
               .map(() => {})
               .catch(this.handleError);
  }

  // Add Single New User to a Group
  public addUserToGroup(programId: number, groupId: number, body: any): Observable<Response> {
    return this.api.post(`${this.programsUrl}/${programId}/groups/${groupId}/users`, body)
               .map(() => {})
               .catch(this.handleError);
  }

  // Get default welcome for group
  public getDefaultWelcome(programId: number, groupId: number): Observable<UserWelcomeMessage> {
    let message = this.api.get(`${this.programsUrl}/${programId}/groups/${groupId}/default_welcome`)
               .map((response: Response) => response.json() as UserWelcomeMessage)
               .publishReplay(1).refCount()
               .catch(this.handleError);

    return message;
  }

  /**
   * Wave Methods
   */

  public addGroup(id: number, group: Group): Promise<Group> {
    return this.api.post(`${this.programsUrl}/${id}/groups`, group)
                   .toPromise()
                   .then(response => response.json())
                   .catch(this.handleError);
  }

  // Get all waves in a Program
  public getWaves(id: number, state?: string): Observable<Wave[]> {
    // if (id !== this._programId) {
      // this._programId = id;
      let url = `${this.programsUrl}/${id}/waves`;
      if(state){
        url += '?state=' + state;
      }
      this._waves = this.api.get(url)
                            .map((response: Response) => response.json() as Wave[])
                            .publishReplay(1).refCount()
                            .catch(this.handleError);
    // }

    return this._waves;
  }

  // Get a single wave in a Program.
  public getWave(programId: number, waveId: number): Observable<WaveSingle> {
    return this.api.get(`${this.programsUrl}/${programId}/waves/${waveId}`)
                   .map(res => res.json())
                   .publishReplay(1).refCount()
                   .catch(this.handleError);
  }


  // Get all groups in a wave
  public getWaveGroups(program_id: number, wave_id: number, state?: string): Observable<Group[]> {
    let url = `${this.programsUrl}/${program_id}/waves/${wave_id}/groups`;
    if(state){
      url += '?state=' + state;
    }
    return this.api.get(url)
                   .map((response: Response) => response.json() as Group[])
                   .publishReplay(1).refCount()
                   .catch(this.handleError);
  }

  public editWave(programId: number, waveId: number, model: Partial<WaveSingle>): Observable<Response> {
    return this.api.patch(`${this.programsUrl}/${programId}/waves/${waveId}`, model)
                   .map((response: Response) => console.log(response))
                   .publishReplay(1).refCount()
                   .catch(this.handleError);
  }

  /**
   * Method for building a form to create or edit a group.
   * Form structure is dependent on whether or not there is a batch reference number.
   */
  public buildForm(batchCommitInfo?: BatchCommitInfo): FormGroup {
    let group: FormGroup;
    if (batchCommitInfo) {
      group = this._fb.group({
        commit_info: batchCommitInfo,
        name: ['', <any>Validators.required],
        analytics_admins: this._fb.array([]),
        timezone: ['', <any>Validators.required],
        start_date: ['', <any>Validators.required],
        end_date: [null, <any>Validators.required],
        enrolment_subject: ['', <any>Validators.required],
        enrolment_message: ['', <any>Validators.required],
        fm_releases: this._fb.array([]),
        fm_release_notification: [false],
        weekly_individual_leaders: [false],
        weekly_top_teams: [false],
        one_week_remaining: [false],
        one_day_remaining: [false],
        notify_users_after_inactivity: [false],
        nudge_delay: [null],
        notify_launch: [false],
        notify_followup: [false],
        notify_followup_delay: [7],
        include_in_all_time_leaders: [true],
        include_in_team_leaders: [true]
      });
    } else {
      group = this._fb.group({
        name: ['', <any>Validators.required],
        analytics_admins: this._fb.array([]),
        timezone: ['', <any>Validators.required],
        start_date: ['', <any>Validators.required],
        end_date: [null, <any>Validators.required],
        enrolment_subject: ['', <any>Validators.required],
        enrolment_message: ['', <any>Validators.required],
        fm_releases: this._fb.array([]),
        fm_release_notification: [false],
        weekly_individual_leaders: [false],
        weekly_top_teams: [false],
        one_week_remaining: [false],
        one_day_remaining: [false],
        notify_users_after_inactivity: [false],
        nudge_delay: [null],
        notify_launch: [false],
        notify_followup: [false],
        notify_followup_delay: [7],
        include_in_all_time_leaders: [true],
        include_in_team_leaders: [true],
        wave_id: ['']
      });
    }

    return group;
  };

  public patchForm(form: FormGroup, settings: GroupSingle): void {
    console.log(form, settings);
    form.patchValue(settings);
  }

  public processRouteForParentId(routeSnapshot: ActivatedRouteSnapshot): number {
    while (routeSnapshot.parent) {
      if (routeSnapshot.parent.params.hasOwnProperty('id')) {
        return +routeSnapshot.parent.params.id;
      } else {
        routeSnapshot = routeSnapshot.parent;
      }
    }
    return null;
  }

  /**
   * Private Methods
   */

  private handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }

}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/dashboard/program-list/group.service.ts